import { doc, getDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { checkPackageExpiration, validateAndUpdateSubscription } from './packageExpirationUtils';
import { PACKAGE_FEATURES, isFeatureEnabled as checkFeatureEnabled } from '../data/packageModels';

/**
 * ตรวจสอบว่า Video Call เปิดใช้งานหรือไม่
 */
export const isVideoCallEnabled = async (storeId) => {
  try {
    // ตรวจสอบ store document
    const storeRef = doc(db, 'stores', storeId);
    const storeSnap = await getDoc(storeRef);
    
    if (!storeSnap.exists()) return { enabled: false, error: 'Store not found' };
    
    const storeData = storeSnap.data();
    let packageType = storeData.packageType || 'free';
    let subscription = null;
    
    // ตรวจสอบ subscription collection
    try {
      const subscriptionsRef = collection(db, 'storeSubscriptions');
      const subscriptionQuery = query(
        subscriptionsRef,
        where('storeId', '==', storeId),
        where('status', '==', 'active')
      );
      const subscriptionSnapshot = await getDocs(subscriptionQuery);
      
      if (!subscriptionSnapshot.empty) {
        const subscriptions = subscriptionSnapshot.docs.map(doc => ({
          id: doc.id,
          ref: doc.ref,
          ...doc.data()
        }));
        
        const sorted = subscriptions.sort((a, b) => {
          const aDate = a.startDate?.toDate?.() || new Date(a.startDate || 0);
          const bDate = b.startDate?.toDate?.() || new Date(b.startDate || 0);
          return bDate - aDate;
        });
        
        subscription = sorted[0];
        packageType = subscription.packageType || 'free';
        
        // ตรวจสอบ expiration
        const expirationCheck = checkPackageExpiration(subscription);
        
        if (expirationCheck.isExpired) {
          await validateAndUpdateSubscription(subscription, subscription.ref);
          return { enabled: false, error: 'Package expired' };
        }
      }
    } catch (error) {
      console.warn('Error checking subscription:', error);
    }
    
    // ตรวจสอบว่า package รองรับ Video Call หรือไม่
    if (!checkFeatureEnabled(packageType, PACKAGE_FEATURES.VIDEO_CALL_ENABLED, subscription)) {
      return { enabled: false, error: 'Video Call not enabled in package' };
    }
    
    // ตรวจสอบ schedule
    const scheduleRef = doc(db, 'videoCallSchedules', storeId);
    const scheduleSnap = await getDoc(scheduleRef);
    
    if (!scheduleSnap.exists()) return { enabled: false, error: 'Schedule not configured' };
    
    const data = scheduleSnap.data();
    return { enabled: data.isActive || false, schedule: data, packageType };
  } catch (error) {
    console.error('Error checking video call enabled:', error);
    return { enabled: false, error: error.message };
  }
};

/**
 * ตรวจสอบว่า Video Call พร้อมใช้งานตอนนี้หรือไม่
 */
export const isVideoCallAvailableNow = async (storeId, productId = null) => {
  try {
    const result = await isVideoCallEnabled(storeId);
    if (!result.enabled) {
      return { available: false, reason: 'Video Call is not enabled' };
    }

    const schedule = result.schedule;
    const now = new Date();
    const currentDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][now.getDay()];
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    // Check schedule based on type
    let timeSlots = [];
    if (schedule.type === 'global') {
      timeSlots = schedule.schedule[currentDay] || [];
    } else if (schedule.type === 'product_specific' && productId) {
      timeSlots = schedule.productSchedules[productId]?.schedule[currentDay] || [];
    } else if (schedule.type === 'flexible') {
      const todayStr = now.toISOString().split('T')[0];
      timeSlots = schedule.dailySchedules[todayStr] || [];
    }

    // Check if current time is within any time slot
    const isInTimeSlot = timeSlots.some(slot => {
      return currentTime >= slot.start && currentTime <= slot.end;
    });

    if (!isInTimeSlot) {
      // Find next available time
      const nextAvailable = findNextAvailableTime(schedule, currentDay, currentTime, productId);
      return {
        available: false,
        reason: 'Not in scheduled time',
        nextAvailable: nextAvailable
      };
    }

    // Check available slots
    const slotsInfo = await getAvailableSlots(storeId, now);
    if (slotsInfo.availableSlots <= 0) {
      return {
        available: false,
        reason: 'No slots available',
        nextAvailable: slotsInfo.nextAvailableTime
      };
    }

    return {
      available: true,
      slotsRemaining: slotsInfo.availableSlots,
      schedule: schedule
    };
  } catch (error) {
    console.error('Error checking video call availability:', error);
    return { available: false, error: error.message };
  }
};

/**
 * หาเวลาที่พร้อมใช้งานถัดไป
 */
const findNextAvailableTime = (schedule, currentDay, currentTime, productId = null) => {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const currentDayIndex = days.indexOf(currentDay);
  
  // Check today first
  let timeSlots = [];
  if (schedule.type === 'global') {
    timeSlots = schedule.schedule[currentDay] || [];
  } else if (schedule.type === 'product_specific' && productId) {
    timeSlots = schedule.productSchedules[productId]?.schedule[currentDay] || [];
  }

  // Find next slot today
  for (const slot of timeSlots) {
    if (slot.start > currentTime) {
      return {
        date: 'Today',
        time: `${slot.start} - ${slot.end}`
      };
    }
  }

  // Check next 7 days
  for (let i = 1; i <= 7; i++) {
    const nextDayIndex = (currentDayIndex + i) % 7;
    const nextDay = days[nextDayIndex];
    
    if (schedule.type === 'global') {
      timeSlots = schedule.schedule[nextDay] || [];
    } else if (schedule.type === 'product_specific' && productId) {
      timeSlots = schedule.productSchedules[productId]?.schedule[nextDay] || [];
    }

    if (timeSlots.length > 0) {
      const firstSlot = timeSlots[0];
      const dayLabel = i === 1 ? 'Tomorrow' : `In ${i} days`;
      return {
        date: dayLabel,
        time: `${firstSlot.start} - ${firstSlot.end}`
      };
    }
  }

  return null;
};

/**
 * ตรวจสอบจำนวน slot ที่ว่าง
 */
export const getAvailableSlots = async (storeId, date = new Date()) => {
  try {
    const scheduleRef = doc(db, 'videoCallSchedules', storeId);
    const scheduleSnap = await getDoc(scheduleRef);
    
    if (!scheduleSnap.exists()) {
      return { availableSlots: 0, maxConcurrentCalls: 0 };
    }

    const schedule = scheduleSnap.data();
    const maxConcurrent = schedule.maxConcurrentCalls || 2;

    // Count active calls at this time
    const requestsRef = collection(db, 'videoCallRequests');
    const activeQuery = query(
      requestsRef,
      where('storeId', '==', storeId),
      where('status', 'in', ['scheduled', 'active'])
    );
    
    const activeSnapshot = await getDocs(activeQuery);
    const activeCalls = activeSnapshot.docs.length;

    const availableSlots = Math.max(0, maxConcurrent - activeCalls);

    return {
      availableSlots,
      maxConcurrentCalls: maxConcurrent,
      activeCalls
    };
  } catch (error) {
    console.error('Error getting available slots:', error);
    return { availableSlots: 0, error: error.message };
  }
};

/**
 * ดึงเวลาที่ว่างสำหรับการจอง
 */
export const getAvailableTimeSlots = async (storeId, productId = null, date = new Date()) => {
  try {
    const scheduleRef = doc(db, 'videoCallSchedules', storeId);
    const scheduleSnap = await getDoc(scheduleRef);
    
    if (!scheduleSnap.exists()) {
      // ถ้ายังไม่มี schedule → return empty array
      // จะแสดงข้อความให้ร้านค้าตั้งค่า schedule
      return [];
    }

    const schedule = scheduleSnap.data();
    const dateStr = date.toISOString().split('T')[0];
    const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][date.getDay()];

    let timeSlots = [];
    if (schedule.type === 'global') {
      timeSlots = schedule.schedule[dayName] || [];
    } else if (schedule.type === 'product_specific' && productId) {
      timeSlots = schedule.productSchedules[productId]?.schedule[dayName] || [];
    } else if (schedule.type === 'flexible') {
      timeSlots = schedule.dailySchedules[dateStr] || [];
    }

    // Get booked slots
    const requestsRef = collection(db, 'videoCallRequests');
    const bookedQuery = query(
      requestsRef,
      where('storeId', '==', storeId),
      where('status', 'in', ['pending', 'approved', 'scheduled'])
    );
    
    const bookedSnapshot = await getDocs(bookedQuery);
    const bookedSlots = bookedSnapshot.docs.map(doc => {
      const data = doc.data();
      const scheduledTime = data.scheduledTime || data.requestedTime;
      if (scheduledTime) {
        const slotDate = scheduledTime.toDate ? scheduledTime.toDate() : new Date(scheduledTime);
        return slotDate.toISOString().split('T')[0];
      }
      return null;
    }).filter(Boolean);

    // Filter available slots
    const availableSlots = timeSlots.map(slot => {
      const slotDateTime = new Date(`${dateStr}T${slot.start}`);
      const isBooked = bookedSlots.includes(dateStr);
      
      return {
        start: slot.start,
        end: slot.end,
        date: dateStr,
        dateTime: slotDateTime,
        available: !isBooked,
        slotsRemaining: schedule.maxConcurrentCalls || 2
      };
    });

    return availableSlots;
  } catch (error) {
    console.error('Error getting available time slots:', error);
    return [];
  }
};

/**
 * ตรวจสอบ Package/Add-on สำหรับ Video Call
 * สำหรับผู้ซื้อ: ตรวจสอบ user package (plan/tier/subscription)
 * สำหรับผู้ขาย: ตรวจสอบ store package
 */
export const checkVideoCallPackage = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      return { hasPackage: false, callsRemaining: 0 };
    }

    const userData = userSnap.data();
    const userRole = userData?.role || 'customer';
    
    // ตรวจสอบ user package ก่อน (สำหรับผู้ซื้อ)
    // user package อาจเก็บไว้ใน plan, tier, หรือ subscription field
    const userPackageType = userData?.plan || userData?.tier || userData?.subscription || null;
    
    console.log('🔍 Video Call Package Check:', {
      userId,
      role: userRole,
      plan: userData?.plan,
      tier: userData?.tier,
      subscription: userData?.subscription,
      userPackageType,
      storeId: userData?.storeId
    });
    
    if (userPackageType && userPackageType !== 'free' && userPackageType !== 'starter') {
      // User มี package (สำหรับผู้ซื้อ)
      // Map package type ตาม role
      let callsLimit = 0;
      
      // สำหรับทุก role: basic = 10 calls
      if (userPackageType === 'basic') {
        callsLimit = 10;
      }
      // สำหรับ role 'girl': premium = 20 calls
      else if (userPackageType === 'premium' && userRole === 'girl') {
        callsLimit = 20;
      }
      // สำหรับ role 'customer': silver/gold/platinum = 15/20/unlimited
      else if (userRole === 'customer') {
        if (userPackageType === 'silver') callsLimit = 15;
        else if (userPackageType === 'gold') callsLimit = 20;
        else if (userPackageType === 'platinum') callsLimit = -1; // unlimited
      }
      // สำหรับ role 'momo': pro/enterprise = 20/unlimited
      else if (userRole === 'momo') {
        if (userPackageType === 'pro') callsLimit = 20;
        else if (userPackageType === 'enterprise' || userPackageType === 'ultraEnterprise') callsLimit = -1; // unlimited
      }
      // สำหรับ role 'girl': vip = unlimited
      else if (userRole === 'girl' && userPackageType === 'vip') {
        callsLimit = -1; // unlimited
      }
      // Default: ถ้าเป็น premium/vip/enterprise/gold/platinum = 20 calls
      else if (['premium', 'vip', 'enterprise', 'gold', 'platinum'].includes(userPackageType)) {
        callsLimit = 20;
      }
      
      // Get usage this month (ใช้ userId)
      const { getVideoCallUsage } = await import('./videoCallDatabase');
      const usage = await getVideoCallUsage(userId, true);
      const callsUsed = usage.totalCalls || 0;
      const callsRemaining = callsLimit === -1 ? -1 : Math.max(0, callsLimit - callsUsed);

      return {
        hasPackage: true,
        packageType: userPackageType,
        callsLimit,
        callsUsed,
        callsRemaining,
        isUserPackage: true
      };
    }

    // ถ้าไม่มี user package ให้ตรวจสอบ store package (สำหรับผู้ขาย)
    const storeId = userData.storeId;

    if (!storeId) {
      return { hasPackage: false, callsRemaining: 0 };
    }

    // Get store subscription - ตรวจสอบจาก stores collection ก่อน
    const storeRef = doc(db, 'stores', storeId);
    const storeSnap = await getDoc(storeRef);
    
    let packageType = 'free';
    
    if (storeSnap.exists()) {
      const storeData = storeSnap.data();
      if (storeData.packageType) {
        packageType = storeData.packageType;
      }
    }
    
    // ถ้า store ไม่มี packageType ให้ตรวจสอบ storeSubscriptions collection
    let subscription = null;
    if (packageType === 'free') {
      try {
        const subscriptionsRef = collection(db, 'storeSubscriptions');
        const subscriptionQuery = query(
          subscriptionsRef,
          where('storeId', '==', storeId),
          where('status', '==', 'active')
        );
        const subscriptionSnapshot = await getDocs(subscriptionQuery);
        
        if (!subscriptionSnapshot.empty) {
          const subscriptions = subscriptionSnapshot.docs.map(doc => ({
            id: doc.id,
            ref: doc.ref,
            ...doc.data()
          }));
          
          // เรียงตาม startDate (ล่าสุดก่อน)
          const sorted = subscriptions.sort((a, b) => {
            const aDate = a.startDate?.toDate?.() || new Date(a.startDate || 0);
            const bDate = b.startDate?.toDate?.() || new Date(b.startDate || 0);
            return bDate - aDate;
          });
          
          subscription = sorted[0];
          packageType = subscription.packageType || 'free';
        }
      } catch (error) {
        console.warn('Error fetching store subscription:', error);
      }
    }
    
    // ตรวจสอบวันสิ้นสุดของ package (ถ้ามี subscription)
    if (subscription) {
      const { checkPackageExpiration, validateAndUpdateSubscription } = await import('./packageExpirationUtils');
      const expirationCheck = checkPackageExpiration(subscription);
      
      // ถ้าหมดอายุ ให้อัพเดท status และ return ไม่มี package
      if (expirationCheck.isExpired) {
        await validateAndUpdateSubscription(subscription, subscription.ref);
        return { hasPackage: false, callsRemaining: 0, packageType: 'free' };
      }
    }

    // Get video call addon if exists (สำหรับ store)
    const addonRef = collection(db, 'storeVideoCallAddons');
    const addonQuery = query(
      addonRef,
      where('storeId', '==', storeId),
      where('status', '==', 'active')
    );
    const addonSnapshot = await getDocs(addonQuery);
    const addon = addonSnapshot.docs[0]?.data();

    // Calculate calls limit (สำหรับ store package)
    let callsLimit = 0;
    if (packageType === 'basic') callsLimit = 10;
    else if (packageType === 'premium') callsLimit = 30;
    else if (packageType === 'enterprise') callsLimit = -1; // unlimited

    if (addon) {
      const addonLimit = addon.callsPerMonth || 0;
      if (callsLimit === -1 || addonLimit === -1) {
        callsLimit = -1; // unlimited
      } else {
        callsLimit += addonLimit;
      }
    }

    // Get usage this month (ใช้ storeId สำหรับ store package)
    const { getVideoCallUsage } = await import('./videoCallDatabase');
    const usage = await getVideoCallUsage(storeId, false);
    const callsUsed = usage.totalCalls || 0;
    const callsRemaining = callsLimit === -1 ? -1 : Math.max(0, callsLimit - callsUsed);

    return {
      hasPackage: packageType !== 'free',
      packageType,
      callsLimit,
      callsUsed,
      callsRemaining,
      addon: addon ? addon.addonId : null,
      isUserPackage: false
    };
  } catch (error) {
    console.error('Error checking video call package:', error);
    return { hasPackage: false, callsRemaining: 0, error: error.message };
  }
};

